import { supabase } from '../lib/supabase';

export interface SaleRecord {
  id: string;
  name: string;
  value: number;
  sale_date: string;
  sale_year: number;
  plates: number;
  panel_power?: number;
  inverter1_power?: number;
  inverter2_power?: number;
  kwp?: number;
}

export interface MonthlySalesData {
  month: number;
  year: number;
  totalValue: number;
  totalSales: number;
  totalPlates: number;
  totalKwp: number;
  avgTicket: number;
}

export interface QuarterlySalesData {
  quarter: number;
  year: number;
  totalValue: number;
  totalSales: number;
  totalPlates: number;
  totalKwp: number;
  avgTicket: number;
}

export interface YearlySalesData {
  year: number;
  totalValue: number;
  totalSales: number;
  totalPlates: number;
  totalKwp: number;
  avgTicket: number;
}

export interface SalesComparisonData {
  currentPeriod: MonthlySalesData[];
  previousYear: MonthlySalesData[];
  previousQuarter: MonthlySalesData[];
  historicalYears: { [year: number]: MonthlySalesData[] };
}

export class SalesDataService {
  static async fetchAllSales(): Promise<SaleRecord[]> {
    const salesRecords: SaleRecord[] = [];

    const { data: projects, error: projectsError } = await supabase
      .from('projects')
      .select('id, name, value, sale_date, sale_year, plates, panel_power, inverter1_power, inverter2_power')
      .not('sale_date', 'is', null)
      .order('sale_date', { ascending: true });

    if (projectsError) {
      console.error('Error fetching projects:', projectsError);
      throw projectsError;
    }

    projects?.forEach(p => {
      const panelPower = parseFloat(p.panel_power || '0');
      const plates = p.plates || 0;
      const inverter1 = parseFloat(p.inverter1_power || '0');
      const inverter2 = parseFloat(p.inverter2_power || '0');

      // Calcular kWp: pode ser baseado nas placas ou nos inversores
      let kwp = 0;
      if (plates > 0 && panelPower > 0) {
        kwp = (plates * panelPower) / 1000; // converter W para kW
      } else if (inverter1 > 0) {
        kwp = inverter1 + (inverter2 || 0);
      }

      salesRecords.push({
        id: p.id,
        name: p.name,
        value: parseFloat(p.value || '0'),
        sale_date: p.sale_date,
        sale_year: p.sale_year || new Date(p.sale_date).getFullYear(),
        plates: plates,
        panel_power: panelPower,
        inverter1_power: inverter1,
        inverter2_power: inverter2,
        kwp: kwp
      });
    });

    const { data: contracts, error: contractsError } = await supabase
      .from('financial_terms')
      .select(`
        id,
        total_amount,
        created_at,
        customer_id,
        customers!inner(
          full_name
        )
      `)
      .order('created_at', { ascending: true });

    if (contractsError) {
      console.error('Error fetching contracts:', contractsError);
      throw contractsError;
    }

    const { data: technicalData } = await supabase
      .from('technical_configs')
      .select('customer_id, solar_modules_quantity, solar_modules_power, inverter1_power, inverter2_power');

    const technicalMap = new Map();
    technicalData?.forEach(tech => {
      technicalMap.set(tech.customer_id, tech);
    });

    contracts?.forEach(c => {
      const tech = technicalMap.get(c.customer_id);
      const plates = tech?.solar_modules_quantity || 0;
      const panelPower = parseFloat(tech?.solar_modules_power || '0');
      const inverter1 = parseFloat(tech?.inverter1_power || '0');
      const inverter2 = parseFloat(tech?.inverter2_power || '0');

      let kwp = 0;
      if (plates > 0 && panelPower > 0) {
        kwp = (plates * panelPower) / 1000;
      } else if (inverter1 > 0) {
        kwp = inverter1 + (inverter2 || 0);
      }

      salesRecords.push({
        id: c.id,
        name: c.customers.full_name,
        value: parseFloat(c.total_amount || '0'),
        sale_date: c.created_at,
        sale_year: new Date(c.created_at).getFullYear(),
        plates: plates,
        panel_power: panelPower,
        inverter1_power: inverter1,
        inverter2_power: inverter2,
        kwp: kwp
      });
    });

    salesRecords.sort((a, b) =>
      new Date(a.sale_date).getTime() - new Date(b.sale_date).getTime()
    );

    return salesRecords;
  }

  static aggregateByMonth(sales: SaleRecord[]): MonthlySalesData[] {
    const monthlyMap = new Map<string, MonthlySalesData>();

    sales.forEach(sale => {
      if (!sale.sale_date) return;

      const date = new Date(sale.sale_date);
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      const key = `${year}-${month}`;

      if (!monthlyMap.has(key)) {
        monthlyMap.set(key, {
          month,
          year,
          totalValue: 0,
          totalSales: 0,
          totalPlates: 0,
          totalKwp: 0,
          avgTicket: 0
        });
      }

      const current = monthlyMap.get(key)!;
      current.totalValue += sale.value;
      current.totalSales += 1;
      current.totalPlates += sale.plates || 0;
      current.totalKwp += sale.kwp || 0;
    });

    const result = Array.from(monthlyMap.values());
    result.forEach(data => {
      data.avgTicket = data.totalSales > 0 ? data.totalValue / data.totalSales : 0;
    });

    return result.sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });
  }

  static aggregateByQuarter(sales: SaleRecord[]): QuarterlySalesData[] {
    const quarterlyMap = new Map<string, QuarterlySalesData>();

    sales.forEach(sale => {
      if (!sale.sale_date) return;

      const date = new Date(sale.sale_date);
      const month = date.getMonth() + 1;
      const quarter = Math.ceil(month / 3);
      const year = date.getFullYear();
      const key = `${year}-Q${quarter}`;

      if (!quarterlyMap.has(key)) {
        quarterlyMap.set(key, {
          quarter,
          year,
          totalValue: 0,
          totalSales: 0,
          totalPlates: 0,
          totalKwp: 0,
          avgTicket: 0
        });
      }

      const current = quarterlyMap.get(key)!;
      current.totalValue += sale.value;
      current.totalSales += 1;
      current.totalPlates += sale.plates || 0;
      current.totalKwp += sale.kwp || 0;
    });

    const result = Array.from(quarterlyMap.values());
    result.forEach(data => {
      data.avgTicket = data.totalSales > 0 ? data.totalValue / data.totalSales : 0;
    });

    return result.sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.quarter - b.quarter;
    });
  }

  static aggregateByYear(sales: SaleRecord[]): YearlySalesData[] {
    const yearlyMap = new Map<number, YearlySalesData>();

    sales.forEach(sale => {
      if (!sale.sale_date) return;

      const year = new Date(sale.sale_date).getFullYear();

      if (!yearlyMap.has(year)) {
        yearlyMap.set(year, {
          year,
          totalValue: 0,
          totalSales: 0,
          totalPlates: 0,
          totalKwp: 0,
          avgTicket: 0
        });
      }

      const current = yearlyMap.get(year)!;
      current.totalValue += sale.value;
      current.totalSales += 1;
      current.totalPlates += sale.plates || 0;
      current.totalKwp += sale.kwp || 0;
    });

    const result = Array.from(yearlyMap.values());
    result.forEach(data => {
      data.avgTicket = data.totalSales > 0 ? data.totalValue / data.totalSales : 0;
    });

    return result.sort((a, b) => a.year - b.year);
  }

  static getMonthsForYear(monthlySales: MonthlySalesData[], year: number): MonthlySalesData[] {
    return monthlySales.filter(m => m.year === year);
  }

  static getQuartersForYear(quarterlySales: QuarterlySalesData[], year: number): QuarterlySalesData[] {
    return quarterlySales.filter(q => q.year === year);
  }

  static calculatePercentageChange(current: number, previous: number): number {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  }

  static getMonthName(month: number): string {
    const months = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    return months[month - 1] || '';
  }

  static getQuarterName(quarter: number): string {
    return `Q${quarter}`;
  }

  static getAvailableYears(sales: SaleRecord[]): number[] {
    const years = new Set<number>();
    sales.forEach(sale => {
      if (sale.sale_date) {
        years.add(new Date(sale.sale_date).getFullYear());
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  }

  static filterSalesByDateRange(
    sales: SaleRecord[],
    startDate: Date,
    endDate: Date
  ): SaleRecord[] {
    return sales.filter(sale => {
      if (!sale.sale_date) return false;
      const saleDate = new Date(sale.sale_date);
      return saleDate >= startDate && saleDate <= endDate;
    });
  }

  static getSameMonthHistoricalData(
    monthlySales: MonthlySalesData[],
    targetMonth: number,
    targetYear: number
  ): MonthlySalesData[] {
    return monthlySales
      .filter(m => m.month === targetMonth && m.year !== targetYear)
      .sort((a, b) => a.year - b.year);
  }

  static getSameQuarterHistoricalData(
    quarterlySales: QuarterlySalesData[],
    targetQuarter: number,
    targetYear: number
  ): QuarterlySalesData[] {
    return quarterlySales
      .filter(q => q.quarter === targetQuarter && q.year !== targetYear)
      .sort((a, b) => a.year - b.year);
  }
}
